import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import surveyService from "../../Services/surveyService";
import "../Survey/AddQuestions.css";
import Footer from "../Footer";
import XPRSurveyLogo from "../../assests/XPRSurveyGreen.png";
import JoditEditor from "jodit-react";
import draftToHtml from "draftjs-to-html";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import NormalAlert from "../Alerts/NormalAlert";
import SurveyTimer from "../Survey/SurveyTimer";

const RenderQuestion = ({ question }) => {
  if (!question) return null;

  if (typeof question === "string" && question.trim().startsWith("<")) {
    return (
      <div
        className="border p-4 rounded-lg bg-gray-50 prose max-w-none"
        dangerouslySetInnerHTML={{ __html: question }}
      />
    );
  }
  try {
    const raw = JSON.parse(question);
    const html = draftToHtml(raw);

    return (
      <div
        className="border p-4 rounded-lg bg-gray-50 prose max-w-none"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  } catch {
    return (
      <div className="border p-4 rounded-lg bg-gray-50 whitespace-pre-wrap">
        {question}
      </div>
    );
  }
};

export default function ParticipantResponse() {
  const { responderUID } = useParams();
  const [surveyData, setSurveyData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(1);
  const [isCaseStudyFullscreen, setIsCaseStudyFullscreen] = useState(false);
  const caseStudyRef = useRef(null);
  const [showQuestionDrawer, setShowQuestionDrawer] = useState(false);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [timerReady, setTimerReady] = useState(false);
  const hasFinishedRef = useRef(false);
  const dbConsumedRef = useRef(0);

  const extractSeconds = (value) => {
    if (!value) return 0;
    return Number(value);
  };

  const navigate = useNavigate();

  //Alert
  const [alert, setAlert] = useState({
    id: 0,
    show: false,
    type: "success",
    message: "",
  });

  const openFullscreen = () => {
    if (caseStudyRef.current?.requestFullscreen) {
      caseStudyRef.current.requestFullscreen();
    }
    setIsCaseStudyFullscreen(true);
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setIsCaseStudyFullscreen(false);
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsCaseStudyFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const showAlert = (type, message) => {
    setAlert({
      id: Date.now(),
      show: true,
      type,
      message,
    });
  };

  const getTotalConsumedTime = () => {
    return dbConsumedRef.current;
  };

  //  Fetch Survey Details
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const surveyResponse =
          await surveyService.GetSurveyDetailsByResponderUID({
            responderUID,
          });

        const survey = surveyResponse.surveyDetails[0];
        setSurveyData(survey);

        const remaining = Number(survey.totalTimeForSurvey) || 0; // already remaining
        const usedSeconds = Number(survey.timeConsumed) || 0;

        dbConsumedRef.current = usedSeconds;

        // DO NOT subtract again
        setRemainingSeconds(remaining);

        setTimerReady(true); //  TIMER DATA READY
      } catch (err) {
        console.error(err);
      }
    };

    fetchSurvey();
  }, [responderUID]);

  useEffect(() => {
    if (step !== 2 || !responderUID) return;

    let isCancelled = false;

    const startSurvey = async () => {
      try {
        //  Start / Resume survey (backend initializes timer here)
        const payload = {
          responderUID,
          isPreviousQuestion: false,
          timeConsumed: dbConsumedRef.current, //  ADD THIS
        };

        console.log("Starting survey:", payload);

        const result = await surveyService.RecordResponse(payload);

        if (
          !isCancelled &&
          result?.status === "Success" &&
          result.response?.length
        ) {
          const question = result.response[0];

          //  Set first question
          setCurrentQuestion({
            questionUID: question.questionID,
            question: question.questionName,
            questionSequence: question.questionSequence,
            totalQuestions: question.totalQuestions,
          });

          setAnswers({
            [question.questionID]: question.answer || "",
          });
        }
      } catch (err) {
        console.error("Failed to start survey:", err);
      }
    };

    startSurvey();

    return () => {
      isCancelled = true;
    };
  }, [step, responderUID]);

  const [caseStudyBlobURL, setCaseStudyBlobURL] = useState(null);
  const [leftWidth, setLeftWidth] = useState(55);
  const isDraggingRef = useRef(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const leftWidthRef = useRef(leftWidth);

  useEffect(() => {
    if (!surveyData?.caseStudy) return;

    const fetchPDF = async () => {
      try {
        const fileName = surveyData.caseStudy.split("/").pop();

        const response = await surveyService.GetCaseStudy(fileName);

        const pdfBlob = new Blob([response], {
          type: "application/pdf",
        });

        const blobURL = URL.createObjectURL(pdfBlob);
        setCaseStudyBlobURL(blobURL);
      } catch (err) {
        console.error("Error fetching case study PDF:", err);
        setCaseStudyBlobURL(null);
      }
    };
    fetchPDF();

    return () => {
      if (caseStudyBlobURL) URL.revokeObjectURL(caseStudyBlobURL);
    };
  }, [surveyData]);

  useEffect(() => {
    let rafId = null;

    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;

      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        const container = document.getElementById("survey-split-container");
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const newLeftWidth = (offsetX / rect.width) * 100;

        if (newLeftWidth > 25 && newLeftWidth < 75) {
          leftWidthRef.current = newLeftWidth;
          setLeftWidth(newLeftWidth);
        }

        rafId = null;
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = "default";
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    leftWidthRef.current = leftWidth;
  }, [leftWidth]);

  const DEFAULT_LEFT_WIDTH = 55;

  // for moving the show case study button to move anywhere in screen
  const [btnPosition, setBtnPosition] = useState({
    x: 20,
    y: window.innerHeight - 100,
  });

  const dragRef = useRef({
    isDragging: false,
    hasDragged: false,
    offsetX: 0,
    offsetY: 0,
  });

  // drag handlers
  const onMouseDown = (e) => {
    dragRef.current.isDragging = true;
    dragRef.current.hasDragged = false;
    dragRef.current.offsetX = e.clientX - btnPosition.x;
    dragRef.current.offsetY = e.clientY - btnPosition.y;
  };

  const onMouseMove = (e) => {
    if (!dragRef.current.isDragging) return;

    const newX = e.clientX - dragRef.current.offsetX;
    const newY = e.clientY - dragRef.current.offsetY;

    const dx = Math.abs(newX - btnPosition.x);
    const dy = Math.abs(newY - btnPosition.y);

    if (dx > 5 || dy > 5) {
      dragRef.current.hasDragged = true;
    }

    setBtnPosition({ x: newX, y: newY });
  };

  const onMouseUp = () => {
    dragRef.current.isDragging = false;
  };

  // lobal listeners
  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const MAX_WORDS = 300;

  const getWordCountFromHtml = (html = "") => {
    if (!html) return 0;

    const plainText = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    const words = plainText.match(/\b\w+\b/g);
    return words ? words.length : 0;
  };

  const getAnsweredCount = () => {
    return Object.values(answers).filter((ans) => {
      if (!ans) return false;
      return getWordCountFromHtml(ans) > 0;
    }).length;
  };

  const validateAnswerLimit = () => {
    const html = answers[currentQuestion?.questionUID] || "";
    const count = getWordCountFromHtml(html);

    if (count > MAX_WORDS) {
      showAlert(
        "error",
        `Maximum ${MAX_WORDS} words allowed. You have written ${count}.`,
      );
      return false;
    }

    return true;
  };

  const checkAndPromptFinish = () => {
    const answeredCount = getAnsweredCount();
    const minimumRequired = surveyData.minimumAnswers || 0;

    if (answeredCount >= minimumRequired) {
      setShowFinishConfirm(true);
      return true;
    }

    return false;
  };

  //Handle Finish Survey
  const handleFinishSurvey = async () => {
    if (hasFinishedRef.current) return;
    hasFinishedRef.current = true;
    const currentQ = currentQuestion;

    try {
      const result = await surveyService.FinishSurvey({
        responderUID,
        userAnswer: answers[currentQ.questionUID] || "",
        questionID: currentQ.questionUID,
      });

      if (result.status === "Success" && result.isEnd) {
        showAlert(
          "success",
          result.thankYouNote || "Survey completed successfully!",
        );
        const surveyResponse =
          await surveyService.GetSurveyDetailsByResponderUID({
            responderUID,
          });

        setSurveyData(surveyResponse.surveyDetails[0]);
        setTimeout(() => {
          setAlert({ show: false, type: "", message: "" });
          setStep(1);
        }, 3000);
      } else {
        showAlert(
          "error",
          result.miniQusReq || result.message || "Survey not yet complete.",
        );
      }
    } catch (err) {
      if (err.response?.data) {
        showAlert(
          "error",
          err.response.data.miniQusReq ||
            err.response.data.message ||
            "Survey not yet complete.",
        );
      } else {
        showAlert("error", "Something went wrong while finishing the survey.");
      }
    }
  };

  const blockStyleFn = () => "draft-block";
  const goToQuestion = async (questionID) => {
    try {
      const result = await surveyService.GoToQuestionResponder({
        responderUID,
        questionID,
      });

      if (result.status === "Success" && result.response?.length > 0) {
        const q = result.response[0];

        //  Close drawer + overlay
        setShowQuestionDrawer(false);
        setExpandedQuestionId(null);

        //  Set selected question
        setCurrentQuestion({
          questionUID: q.questionID,
          question: q.questionName,
          questionSequence: q.questionSequence,
          totalQuestions: q.totalQuestions,
        });

        //  Load saved answer if exists
        setAnswers((prev) => ({
          ...prev,
          [q.questionID]: q.answer || "",
        }));
      }
    } catch (err) {
      console.error("GoToQuestionResponder error:", err);
      showAlert("error", "Unable to navigate to selected question.");
    }
  };

  if (!surveyData)
    return <p className="text-center mt-10">Loading survey data...</p>;

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          {/* LEFT: Company Logo from API */}
          <div className="flex items-center">
            {surveyData?.companyLogo && (
              <img
                src={surveyData.companyLogo}
                alt="Company Logo"
                className="h-10 object-contain"
              />
            )}
          </div>

          {/* RIGHT: Default XPR Logo */}
          <div className="flex items-center">
            <img
              src={XPRSurveyLogo}
              alt="XPR Survey"
              className="h-10 object-contain"
            />
          </div>
        </div>
      </nav>

      {/* Step 1: Intro */}
      {step === 1 && surveyData && (
        <div className="min-h-screen flex flex-col items-center justify-start bg-[#f1f6f3] py-8">
          {/* Heading */}
          <h1 className="text-2xl font-bold text-black px-8 py-3 mb-5 mt-4">
            Welcome
          </h1>
          {/* Browser Recommendation */}
          <div className="mt-3 mb-6 px-5 py-3 rounded-md border border-[#3bb290] bg-[#eaf7f2] text-sm text-[#1f6f5c]">
            <strong>Note:</strong> For the best experience, please use
            <strong> Google Chrome</strong> or
            <strong> Mozilla Firefox</strong> on a<strong> PC or Laptop</strong>
            .
          </div>

          {/* Card */}
          <div className="w-full max-w-7xl bg-white shadow-lg rounded-md p-10 min-h-[24rem] flex flex-col">
            <h2 className="text-xl font-semibold mb-2 text-left">
              {surveyData.assessmentName || "Welcome"}
            </h2>

            <div
              className="text-gray-700 mb-8 text-left prose max-w-none"
              dangerouslySetInnerHTML={{
                __html:
                  surveyData.description ||
                  "<p>We are testing data with a description.</p>",
              }}
            />

            {/* Action Button */}
            <div className="mt-auto flex justify-end">
              {surveyData.isSurveyEnded ? (
                <button
                  disabled
                  className="px-6 py-2 text-[#3bb290] rounded-md font-medium transition"
                >
                  Survey Ended
                </button>
              ) : surveyData.isRespondCollected || Number(surveyData.totalTimeForSurvey) === 0 ? (
                <button
                  disabled
                  className="px-6 py-2 text-[#3bb290] rounded-md font-medium transition"
                >
                  Response Already Captured
                </button>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      setStep(2);
                      const payload = {
                        responderUID: responderUID,
                        isPreviousQuestion: false,
                        timeConsumed: dbConsumedRef.current,
                      };

                      console.log("Continue Survey Payload:", payload);

                      const result =
                        await surveyService.RecordResponse(payload);
                      console.log("Continue Survey Result:", result);

                      if (
                        result.status === "Success" &&
                        result.response?.length > 0
                      ) {
                        const question = result.response[0];
                        setCurrentQuestion({
                          questionUID: question.questionID,
                          question: question.questionName,
                          questionSequence: question.questionSequence,
                          totalQuestions: question.totalQuestions,
                        });

                        setAnswers({
                          [question.questionID]: question.answer || "",
                        });
                      } else {
                        showAlert(
                          "error",
                          "No question available to continue.",
                        );
                      }

                      const results =
                        await surveyService.GetQuestionsForResponder(payload);
                      if (results.status === "Success") {
                        setAllQuestions(results.response);
                      }
                    } catch (error) {
                      console.error("Error on Continue Survey:", error);
                      showAlert("error", "Failed to fetch next question.");
                    }
                  }}
                  className="px-6 py-2 bg-[#3bb290] text-white rounded-md font-medium hover:bg-[#339c7c] transition"
                >
                  {surveyData.isRespondStarted
                    ? "Continue Survey"
                    : "Start Survey"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Step 2: Survey */}
      {step === 2 && timerReady && (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start overflow-y-auto">
          <div
            id="survey-split-container"
            className="w-full bg-white shadow-lg rounded-none md:rounded-2xl p-8 pt-1 flex flex-row h-screen relative select-none"
          >
            {/* Case Study Left Panel */}
            {!isMinimized && (
              <div
                ref={caseStudyRef}
                style={{ width: `${leftWidth}%` }}
                className="border-r border-gray-200 pr-2 flex flex-col overflow-hidden min-w-0 bg-white"
              >
                <div className="sticky top-0 bg-white z-20 border-b pb-2 flex items-center justify-between">
                  <h2 className="text-xl font-bold mt-4 truncate">
                    Survey: {surveyData.assessmentName}
                  </h2>

                  <div className="flex gap-2">
                    {!isCaseStudyFullscreen ? (
                      <button
                        onClick={openFullscreen}
                        className="p-2 rounded-md border text-gray-600 hover:bg-gray-100"
                      >
                        Full Screen
                      </button>
                    ) : (
                      <button
                        onClick={exitFullscreen}
                        className="p-2 rounded-md border text-gray-600 hover:bg-gray-100"
                      >
                        Exit Full Screen
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setIsMinimized(true);
                        setLeftWidth(0);
                      }}
                      className="p-2 rounded-md border text-gray-600 hover:bg-gray-100"
                    >
                      Hide
                    </button>
                  </div>
                </div>

                {/* PDF */}
                <div className="flex flex-col flex-1 overflow-hidden mt-2">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Case Study
                  </h3>

                  <div className="border rounded-lg bg-gray-100 flex-1 overflow-y-auto flex justify-center">
                    {caseStudyBlobURL ? (
                      <iframe
                        src={`${caseStudyBlobURL}#toolbar=0&view=FitH`}
                        className="w-full h-full bg-white"
                      />
                    ) : (
                      <p className="text-gray-500">No case study available</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div
              style={{
                width: `${100 - leftWidth}%`,
              }}
              className="pl-4 flex flex-col min-h-0 overflow-y-auto overflow-x-visible"
            >
              <div className="participant-response">
                {currentQuestion ? (
                  <div>
                    <div className="mb-4">
                      <div className="flex items-start gap-4 mb-2 flex-wrap">
                        <h4 className="text-gray-700 font-medium flex-1 min-w-0 break-words mt-4">
                          Question {currentQuestion?.questionSequence}
                        </h4>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setShowQuestionDrawer(true)}
                            className="px-4 py-2 rounded-md font-semibold border border-[#3bb290] text-[#3bb290] hover:bg-[#eaf7f2]"
                          >
                            View All Questions
                          </button>
                          {showQuestionDrawer && (
                            <>
                              {/* Overlay */}
                              <div
                                className="fixed inset-0 bg-black/40 z-40"
                                onClick={() => setShowQuestionDrawer(false)}
                              />

                              {/* Drawer */}
                              <div className="fixed top-0 right-0 h-full w-full sm:w-[520px] lg:w-[620px] bg-white z-50 shadow-xl flex flex-col">
                                {/* Header */}
                                <div className="flex items-center justify-between px-4 py-3 border-b">
                                  <h3 className="font-semibold text-lg text-gray-800">
                                    Question Reference
                                  </h3>
                                  <button
                                    onClick={() => setShowQuestionDrawer(false)}
                                    className="text-red-600 font-semibold"
                                  >
                                    Close
                                  </button>
                                </div>

                                {/* Body */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                  {allQuestions.map((q) => {
                                    const isActive =
                                      q.questionID ===
                                      currentQuestion?.questionUID;

                                    return (
                                      <div
                                        key={q.questionID}
                                        className={`border rounded-md p-3 cursor-pointer transition ${isActive ? "border-[#3bb290] bg-[#eaf7f2]" : "bg-gray-50 hover:bg-gray-100"}`}
                                      >
                                        <div className="flex justify-between items-start gap-4">
                                          {/* Question (2-line preview) */}
                                          <div
                                            className="prose max-w-none text-sm line-clamp-2"
                                            dangerouslySetInnerHTML={{
                                              __html: q.questionName,
                                            }}
                                          />

                                          {/* Go To */}
                                          {!isActive && (
                                            <button
                                              onClick={() =>
                                                goToQuestion(q.questionID)
                                              }
                                              className="text-sm font-semibold text-[#3bb290] whitespace-nowrap"
                                            >
                                              Go to
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}

                          {isMinimized && (
                            <button
                              onClick={() => {
                                setIsMinimized(false);
                                setLeftWidth(DEFAULT_LEFT_WIDTH);
                              }}
                              className="px-4 py-2 rounded-md font-semibold bg-[#3bb290] text-white hover:bg-[#339c7c] transition whitespace-nowrap"
                            >
                              Show Case Study
                            </button>
                          )}
                          <div className="flex-shrink-0">
                            {remainingSeconds !== null && (
                              <SurveyTimer
                                totalSeconds={
                                  Number(surveyData.timeConsumed) +
                                  Number(surveyData.totalTimeForSurvey)
                                }
                                initialSeconds={remainingSeconds}
                                onTimeUpdate={(used) => {
                                  dbConsumedRef.current = used;
                                }}
                                onFinish={async () => {
                                  if (hasFinishedRef.current) return;

                                  hasFinishedRef.current = true;

                                  showAlert(
                                    "error",
                                    "Time is up! Survey submitted automatically.",
                                  );

                                  try {
                                    await surveyService.RecordResponse({
                                      responderUID,
                                      userAnswer:
                                        answers[currentQuestion.questionUID] ||
                                        "",
                                      questionID: currentQuestion.questionUID,
                                      isPreviousQuestion: false,
                                      timeConsumed: getTotalConsumedTime(),
                                    });

                                    const surveyResponse =
                                      await surveyService.GetSurveyDetailsByResponderUID(
                                        {
                                          responderUID,
                                        },
                                      );

                                    setSurveyData(
                                      surveyResponse.surveyDetails[0],
                                    );

                                    //  Move to Step 1
                                    setTimeout(() => {
                                      setStep(1);
                                    }, 2000);
                                  } catch (error) {
                                    console.error("Auto submit failed:", error);
                                  }
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <RenderQuestion question={currentQuestion?.question} />
                    </div>
                    {/*  */}
                    {/* Text Editor*/}
                    <div className="border rounded-lg min-h-[500px] p-2 bg-white">
                      <JoditEditor
                        key={currentQuestion?.questionUID}
                        value={answers[currentQuestion?.questionUID] || ""}
                        config={{
                          readonly: false,
                          height: "100%",
                          minHeight: 500,
                          toolbar: true,
                          toolbarSticky: true,
                          toolbarStickyOffset: 0,
                          toolbarAdaptive: false,

                          askBeforePasteHTML: false,
                          askBeforePasteFromWord: false,
                          defaultActionOnPaste: "insert_as_html",

                          buttons: [
                            "bold",
                            "italic",
                            "underline",
                            "strikethrough",
                            "|",
                            "ul",
                            "ol",
                            "|",
                            "outdent",
                            "indent",
                            "|",
                            "table",
                            "link",
                            "|",
                            "align",
                            "|",
                            "undo",
                            "redo",
                          ],
                          showCharsCounter: false,
                          showWordsCounter: false,
                          showXPathInStatusbar: false,

                          disablePlugins: [
                            "speechRecognize",
                            "print",
                            "spellcheck",
                            "video",
                            "media",
                            "image",
                            "file",
                          ],
                        }}
                        onBlur={(content) => {
                          const count = getWordCountFromHtml(content);

                          if (count > MAX_WORDS) {
                            showAlert(
                              "error",
                              `Maximum ${MAX_WORDS} words allowed. You have written ${count}.`,
                            );
                            return;
                          }

                          setAnswers((prev) => ({
                            ...prev,
                            [currentQuestion.questionUID]: content,
                          }));
                        }}
                      />
                    </div>

                    {/* Prev/Next/Finish Button */}
                    <div className="flex justify-end gap-4 mt-4">
                      {/* Previous Button */}
                      <button
                        disabled={currentQuestion?.questionSequence === 1}
                        className={`px-6 py-2 rounded-md font-semibold transition
            ${
              currentQuestion?.questionSequence === 1
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#3bb290] text-white hover:bg-[#339c7c]"
            }
            `}
                        onClick={async () => {
                          if (currentQuestion?.questionSequence === 1) return;
                          const result = await surveyService.RecordResponse({
                            responderUID,
                            userAnswer:
                              answers[currentQuestion.questionUID] || "",
                            questionID: currentQuestion.questionUID,
                            isPreviousQuestion: true,
                            timeConsumed: getTotalConsumedTime(),
                          });

                          if (
                            result.status === "Success" &&
                            result.response?.length > 0
                          ) {
                            const q = result.response[0];

                            setCurrentQuestion({
                              questionUID: q.questionID,
                              question: q.questionName,
                              questionSequence: q.questionSequence,
                              totalQuestions: q.totalQuestions,
                            });

                            setAnswers((prev) => ({
                              ...prev,
                              [q.questionID]: q.answer || "",
                            }));
                          }
                        }}
                      >
                        Previous
                      </button>

                      {/* Next or Finish Button */}
                      {currentQuestion?.questionSequence <
                      currentQuestion?.totalQuestions ? (
                        <>
                          {/* Next Button */}
                          <button
                            className="px-6 py-2 rounded-md font-semibold bg-[#3bb290] text-white hover:bg-[#339c7c]"
                            onClick={async () => {
                              if (!validateAnswerLimit()) return;
                              const result = await surveyService.RecordResponse(
                                {
                                  responderUID,
                                  userAnswer:
                                    answers[currentQuestion.questionUID] || "",
                                  questionID: currentQuestion.questionUID,
                                  isPreviousQuestion: false,
                                  timeConsumed: getTotalConsumedTime(),
                                },
                              );

                              if (
                                result.status === "Success" &&
                                result.response?.length > 0
                              ) {
                                const q = result.response[0];

                                setCurrentQuestion({
                                  questionUID: q.questionID,
                                  question: q.questionName,
                                  questionSequence: q.questionSequence,
                                  totalQuestions: q.totalQuestions,
                                });

                                setAnswers((prev) => ({
                                  ...prev,
                                  [q.questionID]: q.answer || "",
                                }));
                              }
                            }}
                          >
                            Next
                          </button>
                          {/*  */}
                          {/* Skip Button */}
                          <button
                            className="px-6 py-2 rounded-md font-semibold bg-[#3bb290] text-white hover:bg-[#339c7c]"
                            onClick={async () => {
                              const result = await surveyService.RecordResponse(
                                {
                                  responderUID,
                                  userAnswer:
                                    answers[currentQuestion.questionUID] || "",
                                  questionID: currentQuestion.questionUID,
                                  isPreviousQuestion: false,
                                  timeConsumed: getTotalConsumedTime(),
                                },
                              );

                              if (
                                result.status === "Success" &&
                                result.response?.length > 0
                              ) {
                                const q = result.response[0];

                                setCurrentQuestion({
                                  questionUID: q.questionID,
                                  question: q.questionName,
                                  questionSequence: q.questionSequence,
                                  totalQuestions: q.totalQuestions,
                                });

                                setAnswers((prev) => ({
                                  ...prev,
                                  [q.questionID]: q.answer || "",
                                }));
                              }
                            }}
                          >
                            Skip
                          </button>
                        </>
                      ) : (
                        /* Finish Button */
                        <button
                          type="button"
                          className="px-6 py-2 rounded-md font-semibold bg-[#3bb290] text-white hover:bg-[#339c7c]"
                          // onClick={handleFinishSurvey}
                          onClick={() => {
                            if (!validateAnswerLimit()) return;

                            const prompted = checkAndPromptFinish();

                            if (prompted) return;

                            handleFinishSurvey();
                          }}
                        >
                          Finish
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 italic">Loading questions...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showFinishConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[420px] shadow-xl">
            <h3 className="text-lg font-semibold mb-3">Finish Survey?</h3>

            <p className="text-gray-600 mb-6">
              You have answered the minimum required questions.
              <br />
              Do you want to finish the survey now?
            </p>

            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 border rounded-md"
                onClick={() => setShowFinishConfirm(false)}
              >
                No
              </button>

              <button
                className="px-4 py-2 bg-[#3bb290] text-white rounded-md"
                onClick={() => {
                  setShowFinishConfirm(false);
                  handleFinishSurvey(); //  same API
                }}
              >
                Yes, Finish
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />

      <NormalAlert
        key={alert.id}
        show={alert.show}
        type={alert.type}
        message={alert.message}
        position="center"
        onClose={() => setAlert({ ...alert, show: false })}
      />
    </>
  );
}
